import { Plugin, Widget } from 'ckeditor5';

/**
 * Custom CKEditor 5 plugin to preserve custom HTML elements without restructuring.
 * This plugin uses a widget-based approach to prevent CKEditor from
 * splitting nested spans, adding unwanted attributes, or modifying structure.
 */
export default class AgendaElementsPlugin extends Plugin {
  static get pluginName() {
    return 'AgendaElements';
  }

  static get requires() {
    return ['GeneralHtmlSupport', 'Widget'] as const;
  }

  init() {
    const editor = this.editor;
    
    // Define custom element classes that should be preserved as-is
    const customClasses = [
      'agDurCls',
      'agDurLabelCls',
      'presentedByCls',
      'atwho-inserted',
      'assignCls',
      'minDateCls',
      'dueLabelCls',
      'taskTimezoneCls'
    ];

    // Register custom elements as widgets (atomic, non-editable blocks)
    this._defineCustomWidgets(editor);

    // Get the General HTML Support data filter
    const dataFilter = (editor.plugins.get('GeneralHtmlSupport') as any).dataFilter;

    if (dataFilter) {
      // Allow all span elements with full attribute and class preservation
      dataFilter.allowElement('span');
      
      // Allow all attributes on spans to preserve original structure
      dataFilter.allowAttributes({
        name: 'span',
        attributes: true,
        classes: true,
        styles: true
      });

      // Specifically register custom classes
      dataFilter.allowAttributes({
        name: 'span',
        classes: customClasses
      });

      // Allow all common attributes without filtering
      dataFilter.allowAttributes({
        name: 'span',
        attributes: {
          'contenteditable': true,
          'data-atwho-at-query': true,
          'data-original-title': true,
          'title': true,
          'id': true,
          'fkuserid': true,
          'class': true,
          'style': true
        }
      });

      // Allow contenteditable on common elements
      dataFilter.allowAttributes({
        name: /^(span|div|p|a)$/,
        attributes: {
          'contenteditable': true
        }
      });
    }

    // Prevent CKEditor from modifying custom elements during data processing
    this._preventHtmlModification(editor, customClasses);
    
    // Remove unwanted filler elements
    this._removeFillerElements(editor);
  }

  /**
   * Define custom elements as CKEditor widgets
   * This prevents CKEditor from restructuring them
   */
  private _defineCustomWidgets(editor: any) {
    const schema = editor.model.schema;
    const conversion = editor.conversion;

    // Register agDurCls as a widget - this is the complex nested structure
    schema.register('agDurWidget', {
      isObject: true,    // Makes it atomic - won't be split
      isInline: true,    // Inline element
      allowWhere: '$text',
      allowAttributes: ['htmlContent']
    });

    // Upcast: HTML -> Model (preserve raw HTML for agDurCls)
    conversion.for('upcast').elementToElement({
      view: {
        name: 'span',
        classes: 'agDurCls'
      },
      model: (viewElement: any, { writer }: any) => {
        // Store the entire HTML structure as an attribute
        const htmlContent = this._getOuterHtml(viewElement);
        return writer.createElement('agDurWidget', { htmlContent });
      },
      converterPriority: 'high'
    });

    // Downcast: Model -> View (restore raw HTML for agDurCls)
    conversion.for('downcast').elementToElement({
      model: 'agDurWidget',
      view: (modelElement: any, { writer }: any) => {
        const htmlContent = modelElement.getAttribute('htmlContent') || '';

        // Create a raw HTML container that CKEditor won't modify
        const span = writer.createRawElement('span',
          { class: 'agDurCls', contenteditable: 'false' },
          function(domElement: HTMLElement) {
            // Extract inner content (remove outer span tags from stored HTML)
            const innerContent = htmlContent.replace(/^<span[^>]*class="[^"]*agDurCls[^"]*"[^>]*>|<\/span>$/gi, '');
            domElement.innerHTML = innerContent;
          }
        );

        return span;
      },
      converterPriority: 'high'
    });

    // Register other custom widgets
    this._registerSimpleWidget(editor, 'presentedByCls', 'presentedByWidget');
    this._registerSimpleWidget(editor, 'assignCls', 'assignWidget');
    this._registerSimpleWidget(editor, 'minDateCls', 'minDateWidget');
    this._registerSimpleWidget(editor, 'dueLabelCls', 'dueLabelWidget');
    this._registerSimpleWidget(editor, 'taskTimezoneCls', 'taskTimezoneWidget');
    this._registerSimpleWidget(editor, 'atwho-inserted', 'atWhoWidget');
  }

  /**
   * Register a simple widget for a given class
   */
  private _registerSimpleWidget(editor: any, className: string, widgetName: string) {
    const schema = editor.model.schema;
    const conversion = editor.conversion;

    schema.register(widgetName, {
      isObject: true,
      isInline: true,
      allowWhere: '$text',
      allowAttributes: ['htmlContent', 'userId', 'userName']
    });

    conversion.for('upcast').elementToElement({
      view: {
        name: 'span',
        classes: className
      },
      model: (viewElement: any, { writer }: any) => {
        const htmlContent = this._getOuterHtml(viewElement);
        const userId = viewElement.getAttribute('fkuserid') || viewElement.getAttribute('id') || '';
        const userName = this._getTextContent(viewElement);
        return writer.createElement(widgetName, { htmlContent, userId, userName });
      },
      converterPriority: 'high'
    });

    conversion.for('downcast').elementToElement({
      model: widgetName,
      view: (modelElement: any, { writer }: any) => {
        const htmlContent = modelElement.getAttribute('htmlContent') || '';
        return writer.createRawElement('span',
          { class: className, contenteditable: 'false' },
          function(domElement: HTMLElement) {
            // Extract inner content
            const innerContent = htmlContent.replace(/^<span[^>]*>|<\/span>$/gi, '');
            domElement.innerHTML = innerContent;

            // Restore attributes
            const userId = modelElement.getAttribute('userId');
            if (userId) {
              domElement.setAttribute('fkuserid', userId);
              domElement.setAttribute('id', userId);
            }
          }
        );
      },
      converterPriority: 'high'
    });
  }

  /**
   * Get outer HTML of a view element
   */
  private _getOuterHtml(viewElement: any): string {
    try {
      // Render view element to HTML string
      const domConverter = this.editor.editing.view.domConverter;
      const domElement = domConverter.viewToDom(viewElement) as Node;
      if (domElement && domElement.nodeType === Node.ELEMENT_NODE) {
        return (domElement as unknown as HTMLElement).outerHTML;
      }
      return '';
    } catch {
      return '';
    }
  }

  /**
   * Get text content of a view element
   */
  private _getTextContent(viewElement: any): string {
    let text = '';
    try {
      for (const child of viewElement.getChildren()) {
        if (child.is('$text')) {
          text += child.data;
        } else if (child.is('element')) {
          text += this._getTextContent(child);
        }
      }
    } catch {}
    return text;
  }

  /**
   * Prevent HTML modification by intercepting data pipeline
   */
  private _preventHtmlModification(editor: any, customClasses: string[]) {
    const customElementMap = new Map<string, string>();
    let counter = 0;

    // Helper to create placeholders for custom elements
    const createPlaceholder = (html: string): string => {
      const id = `CKE_CUSTOM_${counter++}`;
      customElementMap.set(id, html);
      return `<span data-custom-placeholder="${id}"></span>`;
    };

    // Helper to restore custom elements from placeholders
    const restorePlaceholders = (html: string): string => {
      let result = html;
      customElementMap.forEach((original, id) => {
        const placeholderRegex = new RegExp(`<span data-custom-placeholder="${id}"><\/span>`, 'g');
        result = result.replace(placeholderRegex, original);
      });
      return result;
    };

    // Intercept data input (setData, paste, etc.)
    editor.data.on('toView', (evt: any, data: any) => {
      if (data.dataValue) {
        let html = data.dataValue;
        
        // Replace custom elements with placeholders before processing
        // Match outermost agDurCls spans with nested content
        html = html.replace(
          /<span[^>]*class="[^"]*agDurCls[^"]*"[^>]*>(?:<span[^>]*agDurLabelCls[^>]*>.*?<\/span>.*?|.*?)<\/span>/gi,
          (match: string) => createPlaceholder(match)
        );
        
        // Match other custom elements
        customClasses.forEach(className => {
          if (className !== 'agDurCls' && className !== 'agDurLabelCls') {
            const regex = new RegExp(`<span[^>]*class="[^"]*${className}[^"]*"[^>]*>.*?<\/span>`, 'gi');
            html = html.replace(regex, (match: string) => createPlaceholder(match));
          }
        });
        
        data.dataValue = html;
      }
    }, { priority: 'high' });

    // Intercept data output (getData)
    editor.data.on('toModel', (evt: any, data: any) => {
      // Cleanup happens in the 'get' event
    });

    // Restore custom elements when getting data
    editor.data.on('get', (evt: any, data: any) => {
      if (data.dataValue) {
        data.dataValue = restorePlaceholders(data.dataValue);
      }
    }, { priority: 'lowest' });

    // Also clean up after setData operations
    editor.data.on('set', () => {
      setTimeout(() => {
        this._cleanupCustomElements(editor, customClasses);
      }, 0);
    }, { priority: 'lowest' });

    // Intercept paste to preserve structure
    editor.editing.view.document.on('clipboardInput', (evt: any, data: any) => {
      const content = data.dataTransfer.getData('text/html');
      if (content && this._hasCustomElements(content, customClasses)) {
        // Mark custom elements to prevent modification
        const tempDiv = document.createElement('div');
        tempDiv.innerHTML = content;
        this._preserveCustomElementsInNode(tempDiv, customClasses);
      }
    }, { priority: 'high' });
  }

  /**
   * Remove unwanted filler elements added by CKEditor
   */
  private _removeFillerElements(editor: any) {
    // Remove filler <br> elements on getData
    editor.data.on('get', (_evt: any, data: any) => {
      if (data.dataValue) {
        // Remove <br data-cke-filler="true"> elements
        data.dataValue = data.dataValue.replace(
          /<br\s+data-cke-filler="true"\s*\/?>/gi,
          ''
        );
        data.dataValue = data.dataValue.replace(
          /<br\s+class="ck-filler"\s*\/?>/gi,
          ''
        );
        // Remove empty paragraphs that only contain fillers
        data.dataValue = data.dataValue.replace(
          /<p>\s*<br[^>]*>\s*<\/p>/gi,
          ''
        );
        // Remove empty data-original-title and title attributes
        data.dataValue = data.dataValue.replace(
          /\s*data-original-title=""\s*/gi,
          ''
        );
        data.dataValue = data.dataValue.replace(
          /\s*title=""\s*/gi,
          ''
        );
        // Trim leading/trailing whitespace and empty paragraphs
        data.dataValue = data.dataValue.trim();
      }
    }, { priority: 'lowest' });

    // Remove fillers immediately after setData
    editor.data.on('set', () => {
      setTimeout(() => {
        this._cleanupFillers(editor);
      }, 50); // Increased timeout to ensure DOM is ready
    }, { priority: 'lowest' });

    // Clean up during editing
    editor.editing.view.document.on('render', () => {
      this._cleanupFillers(editor);
    });

    // Prevent filler insertion on empty content
    editor.model.document.on('change:data', () => {
      setTimeout(() => this._cleanupFillers(editor), 0);
    });
  }

  /**
   * Cleanup filler elements from the editing view
   */
  private _cleanupFillers(editor: any) {
    try {
      const editingRoot = editor.editing.view.getDomRoot();
      if (!editingRoot) return;

      // Remove all filler br elements
      const fillers = editingRoot.querySelectorAll('br[data-cke-filler="true"], br.ck-filler');
      fillers.forEach((filler: any) => {
        // Only remove if it's the first child or in an otherwise empty element
        const parent = filler.parentNode;
        if (parent && (filler === parent.firstChild || parent.childNodes.length === 1)) {
          filler.remove();
        }
      });

      // Remove empty paragraphs at the start
      const firstChild = editingRoot.firstChild;
      if (firstChild && firstChild.nodeName === 'P' && !firstChild.textContent?.trim()) {
        firstChild.remove();
      }
    } catch (e) {
      // Silently fail if cleanup encounters issues
    }
  }

  /**
   * Cleanup custom elements to remove unwanted attributes
   */
  private _cleanupCustomElements(editor: any, customClasses: string[]) {
    const editingRoot = editor.editing.view.getDomRoot();
    if (!editingRoot) return;

    customClasses.forEach(className => {
      const elements = editingRoot.querySelectorAll('.' + className);
      elements.forEach((element: any) => {
        // Remove empty attributes added by CKEditor
        if (element.getAttribute('data-original-title') === '') {
          element.removeAttribute('data-original-title');
        }
        if (element.getAttribute('title') === '') {
          element.removeAttribute('title');
        }
      });
    });
  }

  /**
   * Check if content has custom elements
   */
  private _hasCustomElements(html: string, customClasses: string[]): boolean {
    return customClasses.some(className => html.includes(className));
  }

  /**
   * Preserve custom elements in a DOM node
   */
  private _preserveCustomElementsInNode(node: HTMLElement, customClasses: string[]) {
    customClasses.forEach(className => {
      const elements = node.querySelectorAll('.' + className);
      elements.forEach((element: any) => {
        // Mark element to prevent modification
        element.setAttribute('data-custom-element', 'true');
      });
    });
  }

  /**
   * Check if an element is within a custom element
   */
  private _isInCustomElement(element: any): boolean {
    let current = element.parentElement;
    while (current) {
      if (current.classList && (
        current.classList.contains('agDurCls') ||
        current.classList.contains('presentedByCls') ||
        current.classList.contains('assignCls') ||
        current.classList.contains('minDateCls')
      )) {
        return true;
      }
      current = current.parentElement;
    }
    return false;
  }
}
